"use client";

import { QUALITY_LABEL, type ChordEntry } from "@/lib/music/chord-catalog";
import { useChordLabel } from "@/hooks/use-notation";
import {
  BORDER,
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  VIOLET_BRIGHT,
  VIOLET_SOFT,
} from "./palette";
import {
  ChordPracticeModeToggle,
  type ChordPracticeMode,
} from "./chord-practice-mode-toggle";

type Props = {
  entry: ChordEntry;
  effInv: number;
  maxInvIdx: number;
  inversionTitle: string;
  mode: ChordPracticeMode;
  onModeChange?: (mode: ChordPracticeMode) => void;
  onClose?: () => void;
};

export function ChordPracticeGridHeader({
  entry,
  effInv,
  maxInvIdx,
  inversionTitle,
  mode,
  onModeChange,
  onClose,
}: Props) {
  const chordLabel = useChordLabel();

  return (
    <header
      className="relative z-10 flex shrink-0 items-center justify-between"
      style={{ padding: "14px 24px", borderBottom: `1px solid ${BORDER}` }}
    >
      <div className="flex items-center gap-3">
        <span
          style={{
            fontSize: 26,
            fontWeight: 200,
            lineHeight: 1,
            letterSpacing: "-0.04em",
            fontFamily: "Inter, sans-serif",
            background: `linear-gradient(180deg, ${TEXT} 0%, ${TEXT_DIM} 90%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          {chordLabel(entry.symbol)}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            backgroundColor: VIOLET_SOFT,
            color: VIOLET_BRIGHT,
            fontFamily: FONT_MONO,
            padding: "4px 8px",
            borderRadius: 6,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          {QUALITY_LABEL[entry.quality]}
        </span>
        <span
          aria-hidden="true"
          style={{ color: TEXT_DIM, fontSize: 14, fontWeight: 200 }}
        >
          ·
        </span>
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 300,
            color: TEXT,
            letterSpacing: "-0.01em",
          }}
        >
          {inversionTitle}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {onModeChange && (
          <ChordPracticeModeToggle mode={mode} onChange={onModeChange} />
        )}
        <span
          style={{
            fontSize: 11,
            fontFamily: FONT_MONO,
            color: TEXT_DIM,
            letterSpacing: "0.05em",
          }}
        >
          Position {effInv + 1} / {maxInvIdx + 1}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer la pratique"
            className="rounded-full p-1.5 transition-colors hover:bg-white/[0.06]"
            style={{ color: TEXT_DIM }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        )}
      </div>
    </header>
  );
}
